import { useEffect, useState } from "react";
import { colors } from "@/lib/constants";

export interface TileColorAssignment {
  tileIndex: number;
  colorIndex: number;
}

export interface VisionBoardPreferences {
  isVisible: boolean;
  tileColorAssignments: TileColorAssignment[];
}

const STORAGE_KEY = "visionBoardPreferences";

export function useVisionBoardPreferences() {
  const [preferences, setPreferences] = useState<VisionBoardPreferences>(() => {
    if (typeof window === "undefined") {
      return {
        isVisible: false,
        tileColorAssignments: [],
      };
    }

    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) {
        return JSON.parse(saved);
      }
    } catch (error) {
      console.error("Failed to load vision board preferences:", error);
    }

    return {
      isVisible: false,
      tileColorAssignments: [],
    };
  });

  // Save preferences to localStorage whenever they change
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(preferences));
    } catch (error) {
      console.error("Failed to save vision board preferences:", error);
    }
  }, [preferences]);

  const toggleVisibility = () => {
    setPreferences((prev) => ({
      ...prev,
      isVisible: !prev.isVisible,
    }));
  };

  const assignTileColor = (tileIndex: number, colorIndex: number) => {
    setPreferences((prev) => {
      const existingIndex = prev.tileColorAssignments.findIndex(
        (tc) => tc.tileIndex === tileIndex,
      );

      let newAssignments;
      if (existingIndex >= 0) {
        newAssignments = [...prev.tileColorAssignments];
        newAssignments[existingIndex] = { tileIndex, colorIndex };
      } else {
        newAssignments = [
          ...prev.tileColorAssignments,
          { tileIndex, colorIndex },
        ];
      }

      return {
        ...prev,
        tileColorAssignments: newAssignments,
      };
    });
  };

  const getTileColorIndex = (tileIndex: number): number | null => {
    const assignment = preferences.tileColorAssignments.find(
      (tc) => tc.tileIndex === tileIndex,
    );
    return assignment ? assignment.colorIndex : null;
  };

  const getColorForTile = (
    tileIndex: number,
    theme: "light" | "dark",
  ): { color: string; colorIndex: number } => {
    let colorIndex = getTileColorIndex(tileIndex);

    // If no saved color, assign a random one
    if (colorIndex === null) {
      colorIndex = Math.floor(Math.random() * colors.length);
      assignTileColor(tileIndex, colorIndex);
    }

    const colorSet = colors[colorIndex];
    const color = theme === "dark" ? colorSet.dark.value : colorSet.light.value;

    return { color, colorIndex };
  };

  const clearPreferences = () => {
    setPreferences({
      isVisible: true,
      tileColorAssignments: [],
    });
  };

  return {
    preferences,
    isVisible: preferences.isVisible,
    toggleVisibility,
    assignTileColor,
    getTileColorIndex,
    getColorForTile,
    clearPreferences,
  };
}
